import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import Attachments from '../components/Attachments';
import CustomerTags from '../components/CustomerTags';
import CustomerDetails from '../components/customer/CustomerDetails';
import CustomerTimeline from '../components/customer/CustomerTimeline';
import CustomerEditDialog from '../components/customer/CustomerEditDialog';
import { CustomerHeading, KpiStrip, Tabs, EmptyState, formatDate } from '../components/customer/CustomerParts';
import { useIsMobile } from '../hooks/mobile';
import { expiryInfo, nextExpiry, summariseBilling, isOverdueInvoice } from '../utils/customerView';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import roleManager, { PERMISSIONS } from '../utils/roleManager';
import { apiClient } from '../utils/apiClient';
import { formatMoney } from '../utils/currency';
import '../styles/ticketTheme.css';
import '../components/customer/customer.css';
import './Customer.css';

const TAB_KEYS = ['overview', 'tickets', 'billing', 'domains', 'hosting', 'files'];
const statusClass = (status) => `status-${String(status || '').toLowerCase().replace(/[\s_]+/g, '-')}`;

function Customer() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [domains, setDomains] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [hostingAccounts, setHostingAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [detailsOpen, setDetailsOpen] = useState(null);
  const [notice, setNotice] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [noteForm, setNoteForm] = useState({ description: '', internal_external: 'internal', activity_type: 'note' });

  const [showHostingModal, setShowHostingModal] = useState(false);
  const [hostingPackages, setHostingPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [hostingForm, setHostingForm] = useState({ domain: '', username: '', contactemail: '', package: '' });
  const [savingHosting, setSavingHosting] = useState(false);
  const [hostingError, setHostingError] = useState('');
  const [showLinkExistingModal, setShowLinkExistingModal] = useState(false);
  const [unlinkedAccounts, setUnlinkedAccounts] = useState([]);
  const [loadingUnlinked, setLoadingUnlinked] = useState(false);
  const [unlinkedConfigured, setUnlinkedConfigured] = useState(true);
  const [selectedUnlinkedUsername, setSelectedUnlinkedUsername] = useState('');
  const [linkExistingError, setLinkExistingError] = useState('');
  const [linkingExisting, setLinkingExisting] = useState(false);
  const [generatedPasswordInfo, setGeneratedPasswordInfo] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [savingSuspend, setSavingSuspend] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [unsuspendTarget, setUnsuspendTarget] = useState(null);
  const [savingUnsuspend, setSavingUnsuspend] = useState(false);

  const canEdit = roleManager.hasPermission(PERMISSIONS.EDIT_CUSTOMERS);
  const isSysadmin = (Globals.UserType || '').toLowerCase() === 'sysadmin';

  // Each resource loads independently - one failing (a 404 on domains, say)
  // shouldn't stop the rest of the page from loading, so each call catches
  // its own failure rather than sharing one try/catch that would abort the
  // remaining fetches partway through.
  const fetchCustomerData = useCallback(async () => {
    setLoading(true);
    try {
      const customerData = await apiClient.get(`/api/users/${userId}`).catch(() => null);
      if (customerData) setCustomer(customerData);
      else setError('Failed to load customer');

      const ticketsData = await apiClient.get(`/api/tickets/customer/${userId}`).catch(() => null);
      if (ticketsData) setTickets(ticketsData);

      const purchasesData = await apiClient.get(`/api/purchases/user/${userId}`).catch(() => null);
      if (purchasesData) setPurchases(purchasesData);

      const domainsData = await apiClient.get(`/api/domains/user/${userId}`).catch(() => null);
      if (domainsData) setDomains(domainsData);

      // Sysadmin-only on the backend too, so skip the request entirely for
      // anyone else rather than eating a guaranteed 403.
      if (isSysadmin) {
        const hostingData = await apiClient.get(`/api/hosting-accounts/customer/${userId}`).catch(() => null);
        if (hostingData) setHostingAccounts(hostingData);
      }

      const invoicesData = await apiClient.post('/api/invoices/GetInvoices', { user_id: userId }).catch(() => null);
      if (invoicesData) setInvoices(invoicesData);

      const activitiesData = await apiClient.get(`/api/activities/user/${userId}`).catch(() => null);
      if (activitiesData) {
        setNotes(
          activitiesData
            .filter((activity) => !activity.ticket_id)
            .map((activity) => ({
              id: activity.id,
              author: activity.author_name || 'Unknown',
              dateObj: activity.created_at ? new Date(activity.created_at) : new Date(),
              text: activity.description || '',
              internal_external: activity.internal_external || 'internal'
            }))
        );
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }
    if (userId) fetchCustomerData();
  }, [userId, navigate, fetchCustomerData]);

  // Real Date objects throughout, not a mix of pre-formatted DD/MM/YYYY
  // strings (notes) and raw ISO strings (tickets) - that mismatch used to
  // make the combined sort unreliable.
  const getTimeline = () => {
    const inactiveTickets = tickets
      .filter((t) => t.status && ['closed', 'resolved', 'cancelled'].includes(t.status.toLowerCase()))
      .map((t) => ({
        type: 'ticket',
        id: t.ticket_id,
        ticket_id: t.ticket_id,
        dateObj: new Date(t.updated_at || t.created_at),
        text: `Ticket #${t.ticket_number || t.ticket_id}: ${t.subject}`,
        status: t.status,
        author: t.assigned_user_name || 'System'
      }));

    return [...notes, ...inactiveTickets].sort((a, b) => b.dateObj - a.dateObj);
  };


  const handleNoteFormChange = (field, value) => setNoteForm((prev) => ({ ...prev, [field]: value }));

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteForm.description.trim()) return;
    setSavingNote(true);
    try {
      await apiClient.post('/api/activities', {
        customer_id: userId,
        author_user_id: Globals.Id,
        internal_external: noteForm.internal_external,
        description: noteForm.description,
        activity_type: noteForm.activity_type
      });
      setNoteForm({ description: '', internal_external: 'internal', activity_type: 'note' });
      setShowNoteModal(false);
      fetchCustomerData();
    } catch (err) {
      console.error('Error saving note:', err);
      notify('error', 'Failed to save the note. Try again.');
    } finally {
      setSavingNote(false);
    }
  };

  // Quiet confirmations/errors at the top of the page (replaces alert()).
  const notify = (type, message) => {
    setNotice({ type, message });
    if (type === 'ok') setTimeout(() => setNotice((n) => (n && n.message === message ? null : n)), 5000);
  };

  const handleSaved = (updated) => {
    setCustomer((prev) => ({ ...prev, ...updated }));
    setEditOpen(false);
    notify('ok', 'Customer details saved.');
  };

  const openHostingModal = async (prefillDomain) => {
    setHostingError('');
    setHostingForm({
      domain: prefillDomain || '',
      username: '',
      contactemail: customer?.email || '',
      package: ''
    });
    setShowHostingModal(true);
    setLoadingPackages(true);
    try {
      const packages = await apiClient.get('/api/hosting-accounts/packages');
      setHostingPackages(packages || []);
    } catch (err) {
      setHostingError(err.message || 'Failed to load WHM packages');
    } finally {
      setLoadingPackages(false);
    }
  };

  const submitHosting = async (e) => {
    e.preventDefault();
    setHostingError('');
    setSavingHosting(true);
    try {
      const result = await apiClient.post('/api/hosting-accounts', {
        user_id: userId,
        domain: hostingForm.domain,
        username: hostingForm.username,
        contactemail: hostingForm.contactemail,
        package: hostingForm.package
      });
      setHostingAccounts((prev) => [result.account, ...prev]);
      setShowHostingModal(false);
      if (result.generatedPassword) {
        setGeneratedPasswordInfo({ username: hostingForm.username, password: result.generatedPassword });
      }
    } catch (err) {
      setHostingError(err.message || 'Failed to create hosting account');
    } finally {
      setSavingHosting(false);
    }
  };

  // Only ever shows accounts the API itself has already determined have no
  // hosting_accounts row anywhere - so this is safe to reuse across every
  // customer's page without re-filtering client-side.
  const openLinkExistingModal = async () => {
    setLinkExistingError('');
    setSelectedUnlinkedUsername('');
    setShowLinkExistingModal(true);
    setLoadingUnlinked(true);
    try {
      const data = await apiClient.get('/api/hosting-accounts/unlinked');
      setUnlinkedConfigured(data?.configured !== false);
      setUnlinkedAccounts(data?.accounts || []);
    } catch (err) {
      setLinkExistingError(err.message || 'Failed to load unlinked WHM accounts');
    } finally {
      setLoadingUnlinked(false);
    }
  };

  const submitLinkExisting = async (e) => {
    e.preventDefault();
    const account = unlinkedAccounts.find((a) => a.cpanel_username === selectedUnlinkedUsername);
    if (!account) return;

    setLinkExistingError('');
    setLinkingExisting(true);
    try {
      const linked = await apiClient.post('/api/hosting-accounts/link', {
        user_id: userId,
        cpanel_username: account.cpanel_username,
        domain_name: account.domain_name,
        contactemail: account.contactemail,
        whm_package: account.whm_package,
        status: account.status
      });
      setHostingAccounts((prev) => [linked, ...prev]);
      setShowLinkExistingModal(false);
    } catch (err) {
      setLinkExistingError(err.message || 'Failed to link this hosting account');
    } finally {
      setLinkingExisting(false);
    }
  };

  const submitSuspend = async (e) => {
    e.preventDefault();
    if (!suspendReason.trim()) return;
    setSavingSuspend(true);
    try {
      const updated = await apiClient.post(`/api/hosting-accounts/${suspendTarget.id}/suspend`, { reason: suspendReason });
      setHostingAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setSuspendTarget(null);
      setSuspendReason('');
    } catch (err) {
      notify('error', err.message || 'Failed to suspend hosting account');
    } finally {
      setSavingSuspend(false);
    }
  };

  const submitUnsuspend = async (e) => {
    e.preventDefault();
    setSavingUnsuspend(true);
    try {
      const updated = await apiClient.post(`/api/hosting-accounts/${unsuspendTarget.id}/unsuspend`);
      setHostingAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setUnsuspendTarget(null);
    } catch (err) {
      notify('error', err.message || 'Failed to unsuspend hosting account');
    } finally {
      setSavingUnsuspend(false);
    }
  };

  // Reconciles against WHM directly, in case the account was suspended or
  // unsuspended on the server itself rather than through Blade.
  const handleRefreshStatus = async (account) => {
    setRefreshingId(account.id);
    try {
      const updated = await apiClient.post(`/api/hosting-accounts/${account.id}/refresh`);
      setHostingAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (err) {
      notify('error', err.message || 'Failed to refresh hosting account status');
    } finally {
      setRefreshingId(null);
    }
  };

  if (loading) {
    return (
      <PageShell contentClassName="tk">
        <div className="cx-page">
          <div className="cx-skeleton" aria-live="polite" aria-busy="true">
            <span className="sr-only">Loading customer&hellip;</span>
            <div /><div /><div />
          </div>
        </div>
      </PageShell>
    );
  }

  if (error || !customer) {
    return (
      <PageShell contentClassName="tk">
        <div className="cx-page">
          <p className="cx-banner cx-banner-error" role="alert">{error || 'Customer not found.'}</p>
          <Button variant="secondary" onClick={() => navigate('/users')}>Back to customers</Button>
        </div>
      </PageShell>
    );
  }

  const activeTickets = tickets.filter((t) => t.status && !['closed', 'resolved', 'cancelled'].includes(t.status.toLowerCase()));
  const urgentCount = activeTickets.filter((t) => ['urgent', 'high'].includes(String(t.priority || '').toLowerCase())).length;
  const timeline = getTimeline();
  const billing = summariseBilling(invoices);
  const expiring = nextExpiry(domains);
  const suspendedHosting = hostingAccounts.filter((h) => h.status === 'suspended').length;
  const person = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'tickets', label: 'Tickets', count: tickets.length },
    { key: 'billing', label: 'Billing', count: invoices.length },
    { key: 'domains', label: 'Domains', count: domains.length },
    ...(isSysadmin ? [{ key: 'hosting', label: 'Hosting', count: hostingAccounts.length }] : []),
    { key: 'files', label: 'Files' }
  ];
  const requestedTab = searchParams.get('tab');
  const activeTab = tabs.some((t) => t.key === requestedTab) ? requestedTab : 'overview';
  // The tab lives in the URL so a refresh or a shared link lands where you were.
  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'overview') next.delete('tab'); else next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  const kpis = [
    { key: 'tickets', label: 'Open tickets', value: activeTickets.length, sub: activeTickets.length ? (urgentCount ? `${urgentCount} high or urgent` : 'None urgent') : 'All clear', tone: urgentCount ? 'bad' : '', onClick: () => setTab('tickets') },
    { key: 'domains', label: 'Domains', value: domains.length, sub: expiring ? `${expiring.info.label}` : 'No expiry dates', tone: expiring && expiring.info.tone === 'expired' ? 'bad' : (expiring && expiring.info.tone === 'soon' ? 'warn' : ''), onClick: () => setTab('domains') },
    ...(isSysadmin ? [{ key: 'hosting', label: 'Hosting', value: hostingAccounts.length, sub: suspendedHosting ? `${suspendedHosting} suspended` : 'All active', tone: suspendedHosting ? 'warn' : '', onClick: () => setTab('hosting') }] : []),
    { key: 'overdue', label: 'Overdue invoices', value: billing.overdueCount, sub: billing.overdueCount ? formatMoney(billing.overdueTotal) : 'None overdue', tone: billing.overdueCount ? 'bad' : '', onClick: () => setTab('billing') },
    { key: 'paid', label: 'Total paid', value: formatMoney(billing.paidTotal), sub: `${billing.paidCount} paid invoice${billing.paidCount === 1 ? '' : 's'}`, onClick: () => setTab('billing') }
  ];

  const detailsExpanded = detailsOpen === null ? !isMobile : detailsOpen;

  const domainRow = (d) => {
    const hasHostingAccount = hostingAccounts.some((h) => h.domain_name.toLowerCase() === d.domain_name.toLowerCase());
    const exp = expiryInfo(d.expiry_date);
    return (
      <div key={d.id} className="cx-row">
        <div className="cx-row-main">
          <div className="cx-row-title">{d.domain_name}</div>
          <div className="cx-row-sub">Registrar: {d.registrar || 'Not recorded'}</div>
        </div>
        <dl className="cx-meta">
          <div><dt>Registered</dt><dd>{formatDate(d.registered_date)}</dd></div>
          <div><dt>Next due</dt><dd>{formatDate(d.next_due_date)}</dd></div>
          <div><dt>Expires</dt><dd>{formatDate(d.expiry_date)}</dd></div>
        </dl>
        {exp && <span className={`cx-chip ${exp.tone === 'expired' ? 'cx-chip-bad' : exp.tone === 'soon' ? 'cx-chip-warn' : 'cx-chip-muted'}`}>{exp.label}</span>}
        <span className={`status-badge ${statusClass(d.status || 'active')}`}>{d.status || 'Active'}</span>
        {isSysadmin && !hasHostingAccount && (
          <div className="cx-row-actions">
            <Button variant="secondary" onClick={() => openHostingModal(d.domain_name)}>
              <i className="fas fa-server" aria-hidden="true"></i> Create Hosting Account
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageShell contentClassName="tk">
      <div className="cx-page">
        {notice && (
          <p className={`cx-banner cx-banner-${notice.type === 'ok' ? 'ok' : 'error'}`} role={notice.type === 'ok' ? 'status' : 'alert'}>
            <span>{notice.message}</span>
            <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}>&times;</button>
          </p>
        )}

        <header className="cx-header">
          <CustomerHeading customer={customer} />
          <div className="cx-header-actions">
            <Button onClick={() => setShowNoteModal(true)}><i className="fas fa-plus" aria-hidden="true"></i> Add Note</Button>
            {canEdit && <Button variant="secondary" onClick={() => setEditOpen(true)}><i className="fas fa-pen" aria-hidden="true"></i> Edit</Button>}
          </div>
          <div className="cx-tags"><CustomerTags userId={userId} /></div>
        </header>

        <KpiStrip items={kpis} />

        <div className="cx-layout">
          <aside>
            <CustomerDetails customer={customer} open={detailsExpanded} onToggle={() => isMobile && setDetailsOpen(!detailsExpanded)} />
          </aside>

          <main>
            <Tabs tabs={tabs} active={activeTab} onChange={setTab} />

            <div role="tabpanel" id={`cx-panel-${activeTab}`} aria-labelledby={`cx-tab-${activeTab}`}>
              {activeTab === 'overview' && (
                <>
                  {activeTickets.length > 0 && (
                    <section className="cx-section-block">
                      <div className="cx-block-head">
                        <h2>Open tickets</h2>
                        {activeTickets.length > 5 && <button type="button" className="cx-link-btn" onClick={() => setTab('tickets')}>View all {activeTickets.length}</button>}
                      </div>
                      <div className="cx-list">
                        {activeTickets.slice(0, 5).map((t) => (
                          <Link key={t.ticket_id} to={`/tickets/${t.ticket_id}`} className="cx-row">
                            <span className="cx-num">#{t.ticket_number || t.ticket_id}</span>
                            <div className="cx-row-main"><div className="cx-row-title">{t.subject}</div></div>
                            <span className={`status-badge ${statusClass(t.status)}`}>{String(t.status).replace(/_/g, ' ')}</span>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}

                  {purchases.length > 0 && (
                    <section className="cx-section-block">
                      <div className="cx-block-head">
                        <h2>Purchases</h2>
                        {purchases.length > 3 && (
                          <button type="button" className="cx-link-btn" onClick={() => setExpandedProducts(!expandedProducts)}>
                            {expandedProducts ? 'Show fewer' : `Show all ${purchases.length}`}
                          </button>
                        )}
                      </div>
                      <div className="cx-list">
                        {(expandedProducts ? purchases : purchases.slice(0, 3)).map((purchase) => {
                          const content = (
                            <>
                              <div className="cx-row-main">
                                <div className="cx-row-title">{purchase.product_type} {purchase.product_name}</div>
                                {purchase.remarks && <div className="cx-row-sub">{purchase.remarks}</div>}
                              </div>
                              {purchase.invoice_number && <span className="cx-num">{purchase.invoice_number}</span>}
                              <span className="cx-num">{formatMoney(purchase.line_item_price)}</span>
                            </>
                          );
                          return purchase.invoice_number
                            ? <Link key={purchase.id} to={`/invoices/editinvoice/${purchase.invoice_number}`} className="cx-row">{content}</Link>
                            : <div key={purchase.id} className="cx-row">{content}</div>;
                        })}
                      </div>
                    </section>
                  )}

                  <section className="cx-section-block">
                    <div className="cx-block-head"><h2>Activity</h2></div>
                    {timeline.length === 0
                      ? <EmptyState icon="fa-clock-rotate-left" title="No activity yet">Notes you add and tickets that get closed will appear here.</EmptyState>
                      : <CustomerTimeline items={timeline} />}
                  </section>
                </>
              )}

              {activeTab === 'tickets' && (
                tickets.length === 0
                  ? <EmptyState icon="fa-ticket" title="No tickets">This customer hasn't raised any support tickets.</EmptyState>
                  : (
                    <div className="cx-list">
                      {tickets.map((t) => (
                        <Link key={t.ticket_id} to={`/tickets/${t.ticket_id}`} className="cx-row">
                          <span className="cx-num">#{t.ticket_number || t.ticket_id}</span>
                          <div className="cx-row-main">
                            <div className="cx-row-title">{t.subject}</div>
                            <div className="cx-row-sub">Updated {formatDate(t.updated_at || t.created_at)}{t.assigned_user_name ? ` · ${t.assigned_user_name}` : ''}</div>
                          </div>
                          {t.priority && <span className={`priority-badge priority-${String(t.priority).toLowerCase() === 'urgent' ? 'high' : String(t.priority).toLowerCase()}`}>{t.priority}</span>}
                          <span className={`status-badge ${statusClass(t.status)}`}>{String(t.status).replace(/_/g, ' ')}</span>
                        </Link>
                      ))}
                    </div>
                  )
              )}

              {activeTab === 'billing' && (
                <>
                  <div className="cx-billing-tiles">
                    <div className="cx-tile"><span className="cx-kpi-label">Paid</span><div className="cx-kpi-value">{formatMoney(billing.paidTotal)}</div><span className="cx-kpi-sub">{billing.paidCount} invoice{billing.paidCount === 1 ? '' : 's'}</span></div>
                    <div className="cx-tile"><span className="cx-kpi-label">Awaiting payment</span><div className="cx-kpi-value">{billing.pendingCount}</div><span className="cx-kpi-sub">not yet due</span></div>
                    <div className={`cx-tile ${billing.overdueCount ? 'is-bad' : ''}`}><span className="cx-kpi-label">Overdue</span><div className="cx-kpi-value">{billing.overdueCount}</div><span className="cx-kpi-sub">{billing.overdueCount ? formatMoney(billing.overdueTotal) : 'None'}</span></div>
                  </div>
                  {invoices.length === 0
                    ? <EmptyState icon="fa-file-invoice" title="No invoices yet">Invoices raised for this customer will be listed here.</EmptyState>
                    : (
                      <div className="cx-list">
                        {invoices.map((inv) => {
                          const overdue = isOverdueInvoice(inv);
                          return (
                            <Link key={inv.invoice_number} to={`/invoices/editinvoice/${inv.invoice_number}`} className="cx-row">
                              <div className="cx-row-main">
                                <div className="cx-row-title">{inv.invoice_number}</div>
                                <div className="cx-row-sub">Raised {formatDate(inv.creation_date)}</div>
                              </div>
                              <dl className="cx-meta">
                                <div><dt>Due</dt><dd>{formatDate(inv.payment_due_date)}</dd></div>
                                <div><dt>Amount</dt><dd>{formatMoney(inv.amount_payable, inv.currency)}</dd></div>
                              </dl>
                              <span className={`cx-chip ${overdue ? 'cx-chip-bad' : inv.payment_status === 'Paid' ? 'cx-chip-ok' : 'cx-chip-muted'}`}>{overdue ? 'Overdue' : inv.payment_status}</span>
                            </Link>
                          );
                        })}
                        <Link to={`/invoices?user_id=${userId}`} className="cx-link-btn">Open in Invoices</Link>
                      </div>
                    )}
                </>
              )}

              {activeTab === 'domains' && (
                domains.length === 0
                  ? <EmptyState icon="fa-globe" title="No domains on record">Domains registered for this customer will appear here with their renewal dates.</EmptyState>
                  : <div className="cx-list">{domains.map(domainRow)}</div>
              )}

              {activeTab === 'hosting' && isSysadmin && (
                <>
                  <div className="cx-block-head">
                    <h2>Hosting accounts</h2>
                    <div className="cx-row-actions">
                      <Button onClick={() => openHostingModal('')}><i className="fas fa-plus" aria-hidden="true"></i> Create</Button>
                      <Button variant="secondary" onClick={openLinkExistingModal}><i className="fas fa-link" aria-hidden="true"></i> Link existing</Button>
                    </div>
                  </div>
                  {hostingAccounts.length === 0
                    ? <EmptyState icon="fa-server" title="No hosting accounts">Create one for a domain, or link an account that already exists on the server.</EmptyState>
                    : (
                      <div className="cx-list">
                        {hostingAccounts.map((h) => (
                          <div key={h.id} className="cx-row">
                            <div className="cx-row-main">
                              <div className="cx-row-title">{h.domain_name}</div>
                              <div className="cx-row-sub">cPanel user {h.cpanel_username}</div>
                            </div>
                            <dl className="cx-meta">
                              <div><dt>Package</dt><dd>{h.whm_package || '-'}</dd></div>
                              <div><dt>Created</dt><dd>{formatDate(h.createdAt)}</dd></div>
                            </dl>
                            <span className={`cx-chip ${h.status === 'suspended' ? 'cx-chip-bad' : 'cx-chip-ok'}`}>{h.status === 'suspended' ? 'Suspended' : 'Active'}</span>
                            <div className="cx-row-actions">
                              <Button variant="secondary" onClick={() => handleRefreshStatus(h)} disabled={refreshingId === h.id}>
                                <i className="fas fa-rotate" aria-hidden="true"></i> {refreshingId === h.id ? 'Refreshing…' : 'Refresh'}
                              </Button>
                              {h.status === 'suspended'
                                ? <Button variant="secondary" onClick={() => setUnsuspendTarget(h)}>Unsuspend</Button>
                                : <Button variant="secondary" onClick={() => { setSuspendTarget(h); setSuspendReason(''); }}>Suspend</Button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                </>
              )}

              {activeTab === 'files' && <Attachments entityType="customer" entityId={userId} title="Customer Files" />}
            </div>
          </main>
        </div>
      </div>

      <Modal
        open={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        title="Add Note"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowNoteModal(false)}>Cancel</Button>
            <Button type="submit" form="new-note-form" disabled={savingNote || !noteForm.description.trim()}>{savingNote ? 'Saving…' : 'Save Note'}</Button>
          </>
        )}
      >
        <form id="new-note-form" onSubmit={submitNote}>
          <FormField id="note-visibility" label="Visibility">
            <select id="note-visibility" value={noteForm.internal_external} onChange={(e) => handleNoteFormChange('internal_external', e.target.value)}>
              <option value="internal">Internal Only (staff can see)</option>
              <option value="external">External (customer can see)</option>
            </select>
          </FormField>
          <FormField id="note-type" label="Type">
            <select id="note-type" value={noteForm.activity_type} onChange={(e) => handleNoteFormChange('activity_type', e.target.value)}>
              <option value="note">Note</option>
              <option value="call">Phone Call</option>
              <option value="email">Email</option>
              <option value="meeting">Meeting</option>
            </select>
          </FormField>
          <FormField id="note-description" label="Description" required>
            <textarea id="note-description" rows={5} value={noteForm.description} onChange={(e) => handleNoteFormChange('description', e.target.value)} placeholder="Enter your note here..." />
          </FormField>
        </form>
      </Modal>

      <Modal
        open={showHostingModal}
        onClose={() => setShowHostingModal(false)}
        title="Create Hosting Account"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowHostingModal(false)}>Cancel</Button>
            <Button type="submit" form="new-hosting-form" disabled={savingHosting || loadingPackages}>{savingHosting ? 'Creating…' : 'Create Account'}</Button>
          </>
        )}
      >
        <form id="new-hosting-form" onSubmit={submitHosting}>
          {hostingError && <p className="cust-error">{hostingError}</p>}
          <FormField id="hosting-domain" label="Domain" required>
            <input id="hosting-domain" type="text" value={hostingForm.domain} onChange={(e) => setHostingForm({ ...hostingForm, domain: e.target.value })} placeholder="example.com" />
          </FormField>
          <FormField id="hosting-username" label="cPanel Username" required>
            <input id="hosting-username" type="text" value={hostingForm.username} onChange={(e) => setHostingForm({ ...hostingForm, username: e.target.value.toLowerCase() })} placeholder="lowercase, starts with a letter, max 16 chars" />
          </FormField>
          <FormField id="hosting-email" label="Contact Email" required>
            <input id="hosting-email" type="email" value={hostingForm.contactemail} onChange={(e) => setHostingForm({ ...hostingForm, contactemail: e.target.value })} />
          </FormField>
          <FormField id="hosting-package" label="WHM Package" required>
            <select id="hosting-package" value={hostingForm.package} onChange={(e) => setHostingForm({ ...hostingForm, package: e.target.value })} disabled={loadingPackages}>
              <option value="">{loadingPackages ? 'Loading packages…' : 'Select a package'}</option>
              {hostingPackages.map((p) => (
                <option key={p.name} value={p.name}>{p.name}</option>
              ))}
            </select>
          </FormField>
        </form>
      </Modal>

      <Modal
        open={showLinkExistingModal}
        onClose={() => setShowLinkExistingModal(false)}
        title="Link Existing Hosting Account"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowLinkExistingModal(false)}>Cancel</Button>
            <Button type="submit" form="link-existing-hosting-form" disabled={linkingExisting || loadingUnlinked || !selectedUnlinkedUsername}>
              {linkingExisting ? 'Linking…' : 'Link Account'}
            </Button>
          </>
        )}
      >
        <form id="link-existing-hosting-form" onSubmit={submitLinkExisting}>
          {linkExistingError && <p className="cust-error">{linkExistingError}</p>}
          {!unlinkedConfigured && (
            <p className="cust-error">WHM is not configured on this instance - accounts on the server can't be listed until it is.</p>
          )}
          {unlinkedConfigured && !loadingUnlinked && unlinkedAccounts.length === 0 && (
            <p className="cust-empty">Every cPanel account on the server is already linked to a customer.</p>
          )}
          <FormField id="link-existing-account" label="cPanel Account" required>
            <select
              id="link-existing-account"
              value={selectedUnlinkedUsername}
              onChange={(e) => setSelectedUnlinkedUsername(e.target.value)}
              disabled={loadingUnlinked || unlinkedAccounts.length === 0}
            >
              <option value="">{loadingUnlinked ? 'Loading accounts…' : 'Select an account'}</option>
              {unlinkedAccounts.map((a) => (
                <option key={a.cpanel_username} value={a.cpanel_username}>
                  {a.domain_name || a.cpanel_username} ({a.cpanel_username})
                </option>
              ))}
            </select>
          </FormField>
        </form>
      </Modal>

      <Modal
        open={!!suspendTarget}
        onClose={() => setSuspendTarget(null)}
        title={`Suspend ${suspendTarget?.domain_name || ''}`}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setSuspendTarget(null)}>Cancel</Button>
            <Button type="submit" form="suspend-hosting-form" disabled={savingSuspend || !suspendReason.trim()}>{savingSuspend ? 'Suspending…' : 'Suspend Account'}</Button>
          </>
        )}
      >
        <form id="suspend-hosting-form" onSubmit={submitSuspend}>
          <FormField id="suspend-reason" label="Reason" required>
            <textarea id="suspend-reason" rows={3} value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} placeholder="e.g. Invoice #1234 overdue 30 days" />
          </FormField>
        </form>
      </Modal>

      <Modal
        open={!!unsuspendTarget}
        onClose={() => setUnsuspendTarget(null)}
        title={`Unsuspend ${unsuspendTarget?.domain_name || ''}`}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setUnsuspendTarget(null)}>Cancel</Button>
            <Button type="submit" form="unsuspend-hosting-form" disabled={savingUnsuspend}>{savingUnsuspend ? 'Unsuspending…' : 'Unsuspend Account'}</Button>
          </>
        )}
      >
        <form id="unsuspend-hosting-form" onSubmit={submitUnsuspend}>
          <p>Unsuspend the hosting account for <strong>{unsuspendTarget?.domain_name}</strong>?</p>
        </form>
      </Modal>

      <Modal
        open={!!generatedPasswordInfo}
        onClose={() => setGeneratedPasswordInfo(null)}
        title="Hosting Account Created"
        footer={<Button onClick={() => setGeneratedPasswordInfo(null)}>Done</Button>}
      >
        <p>Account <strong>{generatedPasswordInfo?.username}</strong> was created. This password is shown once and is not stored anywhere in Blade - copy it now and pass it to the customer securely.</p>
        <div className="cust-generated-password">
          <code>{generatedPasswordInfo?.password}</code>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigator.clipboard.writeText(generatedPasswordInfo?.password || '')}
          >
            <i className="fas fa-copy" aria-hidden="true"></i> Copy
          </Button>
        </div>
      </Modal>
      <CustomerEditDialog open={editOpen} customer={customer} userId={userId} onClose={() => setEditOpen(false)} onSaved={handleSaved} />
    </PageShell>
  );
}

export default Customer;
